import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { DocumentService, DocumentStatusResponse, PRAReport } from './document.service';

const STORAGE_PREFIX = 'pra_manuscript_id:v1:';

/**
 * Maps PRA report UUID → manuscript `document_id` (e.g. medRxiv/2026/...).
 * In-memory plus sessionStorage so child routes avoid duplicate status calls and survive refresh.
 */
@Injectable({ providedIn: 'root' })
export class DocumentContextService {
  private readonly documentService = inject(DocumentService);
  private readonly memory = new Map<string, string>();

  private norm(uuid: string): string {
    return uuid.trim().toLowerCase();
  }

  private ssKey(uuid: string): string {
    return STORAGE_PREFIX + this.norm(uuid);
  }

  getCachedManuscriptId(uuid: string | null | undefined): string | null {
    if (!uuid?.trim()) return null;
    const k = this.norm(uuid);
    const fromMem = this.memory.get(k);
    if (fromMem) return fromMem;
    try {
      const v = sessionStorage.getItem(this.ssKey(uuid));
      if (v) {
        this.memory.set(k, v);
        return v;
      }
    } catch {
      /* private mode / quota */
    }
    return null;
  }

  rememberManuscriptId(uuid: string, manuscriptId: string): void {
    const m = manuscriptId.trim();
    if (!uuid?.trim() || !m) return;
    const k = this.norm(uuid);
    this.memory.set(k, m);
    try {
      sessionStorage.setItem(this.ssKey(uuid), m);
    } catch {
      /* ignore */
    }
  }

  rememberFromStatus(uuid: string, status: DocumentStatusResponse): void {
    const id = status.document_id?.trim();
    if (id) this.rememberManuscriptId(uuid, id);
  }

  rememberFromReport(doc: PRAReport): void {
    const uuid = (doc.pra_report_uuid ?? doc.pra_report_id ?? '').toString().trim();
    const man = (doc.document_id ?? doc.submission_id ?? '').trim();
    if (uuid && man) this.rememberManuscriptId(uuid, man);
  }

  rememberManyFromReports(docs: PRAReport[]): void {
    for (const d of docs) this.rememberFromReport(d);
  }

  ensureManuscriptId(uuid: string): Observable<string | null> {
    const cached = this.getCachedManuscriptId(uuid);
    if (cached) return of(cached);
    return this.documentService.getDocumentStatus(uuid).pipe(
      map((s) => s.document_id?.trim() || null),
      tap((id) => {
        if (id) this.rememberManuscriptId(uuid, id);
      })
    );
  }

  clear(): void {
    this.memory.clear();
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(STORAGE_PREFIX)) sessionStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }
  }
}
